import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download } from "lucide-react";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Select } from "../../components/Select";
import { api, API_BASE } from "../../api/client";
import type { PurchaseSheet, SalesSheet } from "../../api/types";
import { formatMoney } from "../../lib/money";

// 對帳:銷項(應收)與進項(應付)。
//
// 業主 2026-09-19:「財務 缺了兩個總表 開出去的發票總覽(應收款)
//                透過API收到的電子發票/自行輸入手開發票 (應付款)
//                **不然是要怎麼對帳**」
//
// 發票頁回答的是「我今天要處理什麼」(待開立/已開立/逾期未收)。
// 這一頁回答的是「這一期總共幾張、未稅多少、稅額多少」——
// **那三個數字就是申報書上要填的格子。**
//
// 期間的預設單位是**申報期**不是月:營業稅單月開票、雙月申報,
// 所以 9/30 與 10/1 同一期。用月當預設的話,每次都要自己加兩個月。

type Tab = "sales" | "purchase";

/** 金額一律走 decimal.js。`Number` 算出來的角分會直接印給財務看。 */
function money(value: string | number | undefined): string {
  // 全系統金額一律 0,000,000、四捨五入到整數(業主 2026-10-15)
  return formatMoney(value);
}

/** 六期的中文字樣。1-2 月是第一期。 */
const PERIODS = Array.from({ length: 6 }, (_, i) => ({
  value: i + 1,
  label: `${i * 2 + 1}-${i * 2 + 2} 月`,
}));

function Total({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="min-w-0 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] px-3 py-2">
      <p className="m-0 text-xs text-[var(--text-2)]">
        {label}
        {hint ? <span className="ml-1 text-[var(--text-3)]">{hint}</span> : null}
      </p>
      <p className="tabular m-0 text-lg font-semibold text-[var(--text)]">{value}</p>
    </div>
  );
}

const TH = "px-3 py-2";
const TD = "px-3 py-2 text-sm text-[var(--text-2)]";
const TD_NUM = "tabular px-3 py-2 text-right text-sm text-[var(--text-2)]";

/** `embedded`:放在報表頁的「對帳」分頁裡(2026-09-30 財務整理 2),標題由報表頁給。 */
export function ReconcilePage({ embedded = false }: { embedded?: boolean } = {}) {
  const today = new Date();
  const [tab, setTab] = useState<Tab>("sales");
  const [year, setYear] = useState(today.getFullYear());
  // 預設是「這個月落在哪一期」—— 財務打開這一頁,九成是要看當期。
  const [vatPeriod, setVatPeriod] = useState(Math.floor(today.getMonth() / 2) + 1);

  const params = { period: "vat", year, vat_period: vatPeriod };
  const query = new URLSearchParams({
    period: "vat",
    year: String(year),
    vat_period: String(vatPeriod),
  }).toString();

  const sales = useQuery({
    queryKey: ["reconcile-sales", query],
    queryFn: () => api.get<SalesSheet>("/reports/receivables", params),
    enabled: tab === "sales",
  });
  const purchase = useQuery({
    queryKey: ["reconcile-purchase", query],
    queryFn: () => api.get<PurchaseSheet>("/reports/payables", params),
    enabled: tab === "purchase",
  });

  const sheet = tab === "sales" ? sales.data : purchase.data;
  const busy = tab === "sales" ? sales.isLoading : purchase.isLoading;
  const failed = tab === "sales" ? sales.error : purchase.error;
  const totals = sheet?.totals;
  const path = tab === "sales" ? "receivables" : "payables";

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {embedded ? <span /> : <h2 className="m-0 text-xl font-semibold text-[var(--text)]">對帳</h2>}
        {/* **對帳是拿著檔案做的事**,不是在畫面上一列一列看 ——
            那份檔案要交給會計事務所、要跟銀行對帳單併排。 */}
        <a
          href={`${API_BASE}/reports/${path}.xlsx?${query}`}
          className="inline-flex h-9 items-center gap-2 rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface)] px-3.5 text-sm font-medium text-[var(--text)] hover:bg-[var(--surface-2)]"
        >
          <Download aria-hidden="true" size={16} />
          下載 XLSX
        </a>
      </div>

      <p className="m-0 text-sm text-[var(--text-2)]">
        營業稅是<strong>單月開票、雙月申報</strong>,所以這一頁的期間是申報期不是月份。
        合計那幾格就是申報書上要填的數字。
      </p>

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex gap-1" role="group" aria-label="總表">
          {(
            [
              ["sales", "銷項(應收)"],
              ["purchase", "進項(應付)"],
            ] as [Tab, string][]
          ).map(([value, text]) => (
            <Button
              key={value}
              size="sm"
              variant={tab === value ? "primary" : undefined}
              aria-pressed={tab === value}
              onClick={() => setTab(value)}
            >
              {text}
            </Button>
          ))}
        </div>

        <Input
          label="年度"
          type="number"
          className="w-24"
          value={year}
          onChange={(e) => setYear(Number(e.target.value) || today.getFullYear())}
        />
        <Select
          label="申報期"
          className="w-28"
          value={vatPeriod}
          onChange={(e) => setVatPeriod(Number(e.target.value))}
          options={PERIODS.map((one) => ({ value: String(one.value), label: one.label }))}
        />
        {sheet ? (
          <p className="m-0 pb-1 text-sm text-[var(--text-3)]">{sheet.period_label}</p>
        ) : null}
      </div>

      {busy ? <p className="m-0 text-sm text-[var(--text-3)]">載入中…</p> : null}
      {failed ? (
        <p role="alert" className="m-0 text-sm text-[var(--danger)]">
          載入失敗,請稍後再試。
        </p>
      ) : null}

      {totals ? (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Total label="張數" value={String(totals.count)} />
          <Total label="未稅合計" value={money(totals.net)} />
          {tab === "sales" ? (
            <>
              <Total label="稅額合計" value={money(totals.tax)} />
              <Total label="含稅合計" value={money(totals.gross)} />
              <Total label="已收" value={money(totals.received)} />
              <Total label="未收" value={money(totals.outstanding)} />
            </>
          ) : (
            <>
              <Total label="帳面稅額" value={money(totals.tax)} />
              {/* **申報要填的是這一格。** 二聯式與收據的稅抵不了,
                  把它算進去就是多報 —— 那是會被補稅加罰的錯。 */}
              <Total
                label="可扣抵稅額"
                hint="(申報用)"
                value={money(totals.deductible_tax)}
              />
              <Total label="含稅合計" value={money(totals.gross)} />
            </>
          )}
        </div>
      ) : null}

      {sheet && sheet.rows.length > 0 ? (
        <div className="w-full overflow-x-auto rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)]">
          {tab === "sales" ? (
            <table aria-label="銷項總覽" className="w-full min-w-[56rem] border-collapse text-left">
              <thead>
                <tr className="bg-[var(--surface-2)] text-xs font-medium text-[var(--text-2)]">
                  <th scope="col" className={TH}>發票號碼</th>
                  <th scope="col" className={TH}>開立日</th>
                  <th scope="col" className={TH}>買方統編</th>
                  <th scope="col" className={TH}>買方名稱</th>
                  <th scope="col" className={`${TH} text-right`}>未稅</th>
                  <th scope="col" className={`${TH} text-right`}>稅額</th>
                  <th scope="col" className={`${TH} text-right`}>含稅</th>
                  <th scope="col" className={`${TH} text-right`}>未收</th>
                  <th scope="col" className={TH}>報價單</th>
                </tr>
              </thead>
              <tbody>
                {(sheet as SalesSheet).rows.map((row) => (
                  <tr
                    key={row.invoice_id}
                    className={`border-t border-[var(--border)] ${row.counted ? "" : "opacity-60"}`}
                  >
                    <td className={`tabular ${TD}`}>
                      {row.invoice_no}
                      {/* **作廢的要看得見** —— 不列的話號碼會莫名其妙跳號,
                          對帳的人會以為漏了一張。 */}
                      {!row.counted ? (
                        <span className="ml-2 rounded-[var(--radius)] bg-[var(--surface-2)] px-1.5 py-0.5 text-xs text-[var(--text-3)]">
                          已作廢‧不計入
                        </span>
                      ) : null}
                    </td>
                    <td className={`tabular ${TD}`}>{row.invoice_date ?? ""}</td>
                    <td className={`tabular ${TD}`}>{row.buyer_tax_id}</td>
                    <td className={TD}>{row.buyer_name}</td>
                    <td className={TD_NUM}>{money(row.net)}</td>
                    <td className={TD_NUM}>{money(row.tax)}</td>
                    <td className={TD_NUM}>{money(row.gross)}</td>
                    <td className={TD_NUM}>{money(row.outstanding)}</td>
                    <td className={TD}>{row.quote_no}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table aria-label="進項總覽" className="w-full min-w-[56rem] border-collapse text-left">
              <thead>
                <tr className="bg-[var(--surface-2)] text-xs font-medium text-[var(--text-2)]">
                  <th scope="col" className={TH}>發票號碼</th>
                  <th scope="col" className={TH}>憑證日</th>
                  <th scope="col" className={TH}>賣方統編</th>
                  <th scope="col" className={TH}>賣方名稱</th>
                  <th scope="col" className={TH}>種類</th>
                  <th scope="col" className={`${TH} text-right`}>未稅</th>
                  <th scope="col" className={`${TH} text-right`}>稅額</th>
                  <th scope="col" className={TH}>可扣抵</th>
                  <th scope="col" className={TH}>歸屬</th>
                </tr>
              </thead>
              <tbody>
                {(sheet as PurchaseSheet).rows.map((row) => (
                  <tr key={row.expense_id} className="border-t border-[var(--border)]">
                    <td className={`tabular ${TD}`}>{row.invoice_no}</td>
                    <td className={`tabular ${TD}`}>{row.invoice_date ?? ""}</td>
                    <td className={`tabular ${TD}`}>{row.vendor_tax_id}</td>
                    <td className={TD}>{row.vendor}</td>
                    <td className={TD}>{row.kind_label}</td>
                    <td className={TD_NUM}>{money(row.net)}</td>
                    <td className={TD_NUM}>{money(row.tax)}</td>
                    {/* 不可只靠顏色 —— 這一欄要寫得出字。 */}
                    <td className={TD}>{row.deductible ? "可" : "不可"}</td>
                    {/* **每一筆帳目都要清清楚楚**(業主原話):單號,或他寫的事項。 */}
                    <td className={TD}>{row.belongs_to}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : null}

      {sheet && sheet.rows.length === 0 && !busy ? (
        <p className="m-0 text-sm text-[var(--text-3)]">
          {sheet.period_label}
          {tab === "sales" ? "沒有開出去的發票。" : "沒有收到的憑證。"}
        </p>
      ) : null}
    </div>
  );
}
